/**
 * **使い捨ての資格情報を二重送信させない**ための掛け金（latch）。
 *
 * ## なぜ必要か（2026-08-30 に AWS で踏んだ）
 *
 * 運営者面のログインは 2 段階で、2 段目に渡す `mfaTicket` は **Redis 上の 1 回きり**の
 * 券である（`apps/api/src/routes/auth/common.ts` の `consumeMfaTicket` が成功時に
 * `DEL` する）。同じ券を 2 回送ると 2 回目は必ず `MFA_TICKET_INVALID` になり、
 * 画面には
 *
 *   「認証セッションの有効期限が切れました。もう一度ログインしてください」
 *
 * が出る。**1 回目は成功していて、セッションも張れている**のに、である。
 * 実際のログは以下の形をしていた（成功のあとに同じ券が再送されている）:
 *
 *   POST /auth/ops/login      200
 *   POST /auth/ops/login/mfa  200   ← 成功。セッション確立
 *   GET  /auth/ops/me         200
 *   POST /auth/ops/login/mfa  401 MFA_TICKET_INVALID   ← 1.5 秒後に同じ券を再送
 *   POST /auth/ops/login/mfa  401 MFA_TICKET_INVALID
 *
 * 利用者から見ると「ログインできない」としか見えない。**サーバーは正しい**
 * （使い捨ての券が使い捨てであることは緩めてはいけない）ので、直すのは送る側。
 *
 * ## なぜ `mut.isPending` では足りないか
 *
 * 従来は `if (!mut.isPending) mut.mutate()` だった。これには穴が 2 つある。
 *
 * 1. **成功後を塞いでいない。** 解決すると `isPending` は false に戻るので、
 *    もう一度 submit されれば消費済みの券がそのまま飛ぶ。
 * 2. **同期的でない。** `isPending` が true になるのは再描画の後なので、
 *    同じタスク内で 2 回 submit が起きると両方とも素通りする。
 *    パスワードマネージャは `autocomplete="one-time-code"` を自動入力して
 *    **自動送信**するものがあり、この 2 回目は人間の操作より速い。
 *
 * `useRef` は再描画を待たないので、掛け金は**同期的に**掛かる。
 *
 * ## 使い方
 *
 * ```ts
 * const gate = useSubmitOnce();
 * const mut = useMutation({ mutationFn, onError: gate.release });
 * <form onSubmit={(e) => { e.preventDefault(); if (gate.claim()) mut.mutate(); }}>
 * ```
 *
 * **`onError` で必ず `release` すること。** 券は間違ったコードで弾かれても
 * まだ生きている（サーバーは `MFA_MAX_ATTEMPTS` まで再試行を許す）ので、
 * 打ち間違えた利用者が入力し直せなくなってはいけない。
 * 逆に **`onSuccess` では解放しない**。券はそこで死んでいる。
 */
import * as React from 'react';

export interface SubmitOnce {
  /** 送ってよければ掛け金を掛けて `true`。送信中か成功済みなら `false`（送らない）。 */
  claim: () => boolean;
  /** 失敗したので次の入力を受け付ける。**成功後は呼ばない。** */
  release: () => void;
}

export function useSubmitOnce(): SubmitOnce {
  const claimed = React.useRef(false);
  return React.useMemo<SubmitOnce>(
    () => ({
      claim: () => {
        if (claimed.current) return false;
        claimed.current = true;
        return true;
      },
      release: () => {
        claimed.current = false;
      },
    }),
    [],
  );
}
